export type PreviewLayout = "inline" | "standalone";

/** CSS fit: page width into the preview column. */
export function fitScale(pageWidth: number, containerWidth: number, layout: PreviewLayout): number {
  const cap = layout === "standalone" ? Math.min(containerWidth, 880) : containerWidth;
  const target = Math.max(280, cap - 8);
  const scale = target / pageWidth;
  return Math.min(2, Math.max(0.6, scale));
}

export const MAX_PREVIEW_OUTPUT_SCALE = 2.5;

/** Cap retina scale so a 3x phone does not allocate huge canvases. */
export function pdfOutputScale(dpr: number): number {
  return Math.min(Math.max(dpr || 1, 1), MAX_PREVIEW_OUTPUT_SCALE);
}

export function pdfRenderScale(
  pageWidth: number,
  containerWidth: number,
  layout: PreviewLayout,
  dpr: number,
): { cssScale: number; outputScale: number; renderScale: number } {
  const cssScale = fitScale(pageWidth, containerWidth, layout);
  const outputScale = pdfOutputScale(dpr);
  return { cssScale, outputScale, renderScale: cssScale * outputScale };
}

/** Backing-store size and pdf.js transform so the bitmap is DPR-sharp at CSS layout size. */
export function pdfCanvasBacking(
  cssWidth: number,
  cssHeight: number,
  outputScale: number,
): {
  canvasWidth: number;
  canvasHeight: number;
  cssWidth: number;
  cssHeight: number;
  transform: number[] | undefined;
} {
  return {
    canvasWidth: Math.floor(cssWidth * outputScale),
    canvasHeight: Math.floor(cssHeight * outputScale),
    cssWidth,
    cssHeight,
    transform: outputScale !== 1 ? [outputScale, 0, 0, outputScale, 0, 0] : undefined,
  };
}

export function applyPdfCanvasBacking(
  canvas: HTMLCanvasElement,
  backing: ReturnType<typeof pdfCanvasBacking>,
): void {
  canvas.width = backing.canvasWidth;
  canvas.height = backing.canvasHeight;
  canvas.style.setProperty("width", `${backing.cssWidth}px`, "important");
  canvas.style.setProperty("height", `${backing.cssHeight}px`, "important");
}